import { useEffect, useMemo, useState } from "react";
import { listMessages, WhatsAppMessage } from "../../lib/whatsappMessages";
import { UserFilterDropdown } from "../../components/UserFilterDropdown";
import { useUserRole } from "../../hooks/useUserRole";

const INTENTS = ["", "blog_idea", "support_question", "unclear", "blocked"];

export function WhatsAppLogsView() {
  const [rows, setRows] = useState<WhatsAppMessage[]>([]);
  const [intent, setIntent] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin } = useUserRole();

  const load = () => {
    setLoading(true);
    setError(null);
    listMessages({ intent: intent || undefined, from: from || undefined, to: to || undefined, userId: userId || undefined })
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  // Laadt bij mount en herlaadt zodra een bedrijf/gebruiker geselecteerd of gewist wordt.
  useEffect(() => { load(); }, [userId]);

  const stats = useMemo(() => {
    const byIntent: Record<string, number> = {};
    for (const r of rows) {
      if (r.direction === "in") {
        const k = r.intent ?? "(geen)";
        byIntent[k] = (byIntent[k] ?? 0) + 1;
      }
    }
    return { total: rows.length, inbound: rows.filter((r) => r.direction === "in").length, byIntent };
  }, [rows]);

  return (
    <div className="space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Totaal</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Inkomend</div>
            <div className="text-2xl font-bold text-gray-900">{stats.inbound}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Intent-verdeling (inbound)</div>
            <div className="text-xs space-y-0.5 text-gray-700">
              {Object.entries(stats.byIntent).map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))}
              {Object.keys(stats.byIntent).length === 0 && (
                <span className="text-gray-400">Geen data</span>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <label className="text-sm font-medium text-gray-700">
              Intent
              <select
                className="block mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
              >
                {INTENTS.map((i) => (
                  <option key={i} value={i}>{i || "alle"}</option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Van
              <input
                type="date"
                className="block mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Tot
              <input
                type="date"
                className="block mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            {isAdmin && (
              <label className="text-sm font-medium text-gray-700 min-w-[220px]">
                Bedrijf
                <UserFilterDropdown
                  selectedUserId={userId}
                  onSelectUser={setUserId}
                  className="mt-1"
                />
              </label>
            )}
            <button
              className="px-4 py-2 rounded-lg transition-colors bg-[#231F20] text-white hover:bg-[#3a3536]"
              onClick={load}
            >
              Filter
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#231F20] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Geen berichten gevonden</h3>
            <p className="text-gray-600">Pas de filters aan om resultaten te zien.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tijd</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Richting</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefoon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bericht</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(r.created_at).toLocaleString("nl-NL")}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.direction === "in" ? "in" : "uit"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{r.phone}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.intent ?? ""}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate">{r.body ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
